/* eslint-disable import/no-unused-modules */
import { insuranceCardData } from '@/components/organisms/InsuranceFinderCard/helpers'
import { InsuranceLabels } from '@/enums'
import { getLocalStorageItem, safeDateString } from '@/utils'
import { compareDesc, differenceInDays } from 'date-fns'
import { curry, uniq } from 'ramda'
import { checkCompletedAssessment } from '../EditLocationPage/helpers'
import { LocationHome } from '@/types/location'

export type PaymentPeriod =
  | 'monthly'
  | 'quarterly'
  | 'half-yearly'
  | 'yearly'
  | 'once'
  | 'non-contributory'

export interface Insurance {
  insuranceId?: string
  purchased?: boolean
  categoryId?: string
  startDate?: string
  grossPrice?: number
  originalGrossPrice?: number
  paymentPeriod?: PaymentPeriod
  insuranceLabel?: string
  showInDashboard?: boolean
}

export interface Recommendation {
  categoryId: string
  sended: string
  createdAt?: string
}

export interface LocationData {
  insurances?: Insurance[]
  recommendations?: Recommendation[]
  essentialCategoryId?: string[]
  riskScore?: number
}

export interface Messages {
  expertAllEssentialInsurances: (args: {
    hasNr: number
    totalNr: number
  }) => string
  expertMissingEssentialInsurances: (args: {
    hasNr: number
    totalNr: number
  }) => string
  expertOfferReady: (args: { offerNr: number }) => string
  expertOfferReadyOne: (args: { offerNr: number }) => string
  expertOptimallyInsured: (args: { startDate: string }) => string
  expertOfferInProgress: string
}

export interface InsuranceCardData {
  showInDashboard?: boolean
}

const checkIfProductExists = curry(
  (key: keyof LocationData, data: LocationData, categoryId: string) => {
    const arr = (data[key] as { categoryId?: string }[] | undefined) ?? []
    return arr.some((item) => item.categoryId === categoryId)
  },
)

export const checkIfRecommendationExists = checkIfProductExists(
  'recommendations',
)

const sortRecommendations = (essentialCategories: string[]) => (
  recommendations: Recommendation[] = [],
): Recommendation[] => {
  const essentials = recommendations
    .filter(({ categoryId }) => essentialCategories.includes(categoryId))
    .sort((a, b) => compareDesc(new Date(a.sended), new Date(b.sended)))

  const others = recommendations
    .filter(({ categoryId }) => !essentialCategories.includes(categoryId))
    .sort((a, b) => compareDesc(new Date(a.sended), new Date(b.sended)))

  return [...essentials, ...others]
}

export const transform = (data: LocationHome): LocationHome => {
  return {
    ...data,
    // @ts-expect-error TODO: unify types for LocationData and LocationHome
    recommendations: sortRecommendations(data.essentialCategoryId ?? [])(
      // @ts-expect-error TODO: unify types for LocationData and LocationHome
      data.recommendations,
    ),
  }
}

const getExistingInsurances = (data: LocationData): Insurance[] => {
  return Array.isArray(data.insurances)
    ? data.insurances.filter((i) => Boolean(i.insuranceId))
    : []
}

const getAllPurchasedEssentialInsurances = (
  data: LocationData,
): Insurance[] => {
  const essentials = data.essentialCategoryId ?? []
  return getExistingInsurances(data)
    .filter((i) => Boolean(i.purchased))
    .filter((i) => i.categoryId && essentials.includes(i.categoryId))
}

const getLastStartDate = (data: LocationData): string => {
  const insurances = getAllPurchasedEssentialInsurances(data)
  const withStartDate = insurances.filter(
    (i): i is Insurance & { startDate: string } => !!i.startDate,
  )
  const sorted = [...withStartDate].sort((a, b) =>
    compareDesc(new Date(a.startDate), new Date(b.startDate)),
  )
  const lastInsurance = sorted.length > 0 ? sorted[0] : undefined
  return safeDateString(lastInsurance ? lastInsurance.startDate : '')
}

const checkExcellent = (data: LocationData): boolean => {
  if (data.riskScore !== 100) return false
  const insurances = getExistingInsurances(data)
  return !insurances.some((i) => i.insuranceLabel !== InsuranceLabels.veryGood)
}

export const makeExpertContent = (
  data: LocationData,
  messages: Messages,
): string => {
  if (!(checkCompletedAssessment as any)(data)) {
    const { riskScore = 0, essentialCategoryId = [] } = data
    const essentialInsurancesCount = essentialCategoryId.length

    const i18nKey =
      riskScore === 100
        ? messages.expertAllEssentialInsurances({
            hasNr: essentialInsurancesCount,
            totalNr: essentialInsurancesCount,
          })
        : messages.expertMissingEssentialInsurances({
            hasNr: Math.round((essentialInsurancesCount * riskScore) / 100),
            totalNr: essentialInsurancesCount,
          })

    return i18nKey
  }

  const recommendations = data?.recommendations || []

  if (recommendations.length) {
    return recommendations.length > 1
      ? messages.expertOfferReady({
          offerNr: recommendations.length,
        })
      : messages.expertOfferReadyOne({
          offerNr: recommendations.length,
        })
  }

  if (
    checkExcellent(data) &&
    getAllPurchasedEssentialInsurances(data).length > 0
  )
    return messages.expertOptimallyInsured({
      startDate: getLastStartDate(data),
    })

  return messages.expertOfferInProgress
}

const paymentPeriodMultipliers: Record<PaymentPeriod, number> = {
  monthly: 12,
  quarterly: 4,
  'half-yearly': 2,
  yearly: 1,
  once: 1,
  'non-contributory': 0,
}

export const calcYearlyPrice = (arr: Insurance[]): number => {
  return arr
    .filter((i) => i.grossPrice && i.paymentPeriod)
    .reduce(
      (acc, { grossPrice, paymentPeriod }) =>
        acc + (grossPrice ?? 0) * paymentPeriodMultipliers[paymentPeriod!],
      0,
    )
}

export const calcOriginalYearlyPrice = (arr: Insurance[]): number => {
  return arr
    .filter((i) => i.grossPrice && i.paymentPeriod)
    .reduce(
      (acc, { grossPrice, originalGrossPrice, paymentPeriod }) =>
        acc +
        (originalGrossPrice ?? grossPrice ?? 0) *
          paymentPeriodMultipliers[paymentPeriod!],
      0,
    )
}

export const insuranceFinderSnoozeLocalStorageKey = 'insurance-finder-cards'

const checkIfSnoozed = (categoryId: string): boolean => {
  const snoozed: Record<string, string> =
    getLocalStorageItem(insuranceFinderSnoozeLocalStorageKey) || {}
  if (!snoozed || typeof snoozed !== 'object') return false
  const snoozeDate = snoozed[categoryId]
  return snoozeDate ? new Date() < new Date(snoozeDate) : false
}

export const makeCardData = (data: LocationData): string[] => {
  const dashboardKeys = Object.keys(
    insuranceCardData as Record<string, InsuranceCardData>,
  ).filter(
    (key) =>
      (insuranceCardData as Record<string, InsuranceCardData>)[key]
        .showInDashboard,
  )
  return dashboardKeys.filter(
    (key) =>
      !checkIfSnoozed(key) && !checkIfProductExists('insurances', data, key),
  )
}

export type CategoryCardConfigItem = {
  samplePrice: number
  image: {
    imageUrl: string
    bottom?: string
    right?: string
    aspectRatio?: string
    width?: string
    height?: string
  }
}

export const CategoryCardConfig: Record<string, CategoryCardConfigItem> = {
  LI: {
    samplePrice: 19.99,
    image: {
      imageUrl: 'coverage-card-liability-icon',
      bottom: '-20%',
      right: '-10%',
      width: '260px',
      height: '200px',
    },
  },
  CO: {
    samplePrice: 25.99,
    image: {
      imageUrl: 'coverage-card-content-icon',
      bottom: '-15%',
      right: '-15%',
      width: '320px',
      height: '180px',
    },
  },
  LE: {
    samplePrice: 28.99,
    image: {
      imageUrl: 'coverage-card-legal-icon',
      right: '0%',
      bottom: '-20%',
      width: '200px',
    },
  },
  FL: {
    samplePrice: 23.99,
    image: {
      imageUrl: 'coverage-card-financial-loss-icon',
      bottom: '-30%',
      width: '220px',
    },
  },
  EL: {
    samplePrice: 14.99,
    image: {
      imageUrl: 'coverage-card-electro-icon',
      bottom: '0%',
      right: '-5%',
      width: '240px',
      height: '100px',
    },
  },
  DO: {
    samplePrice: 59.99,
    image: {
      imageUrl: 'coverage-card-do-icon',
      bottom: '-5%',
    },
  },
  CY: {
    samplePrice: 27.99,
    image: {
      imageUrl: 'coverage-card-cyber-icon',
      bottom: '-10%',
      right: '0%',
      height: '140px',
    },
  },
  MI: {
    samplePrice: 29.99,
    image: {
      imageUrl: 'coverage-card-machine-icon',
      bottom: '-30%',
      right: '-15%',
    },
  },
  default: {
    samplePrice: 0,
    image: {
      imageUrl: 'coverage-card-financial-loss-icon',
      bottom: '-30%',
      width: '220px',
    },
  },
}

export const isRecommendationNew = (createdAt: string | Date): boolean => {
  return differenceInDays(new Date(), new Date(createdAt)) < 2
}

const isCategoryCovered = (data: LocationData, categoryId: string): boolean => {
  const recommendations = data?.recommendations || []
  const existingInsurances = getExistingInsurances(data)

  return (
    recommendations.some((r) => r.categoryId === categoryId) ||
    existingInsurances.some((i: Insurance) => i.categoryId === categoryId)
  )
}

const getUncoveredCategories = (
  data: LocationData,
  categoryIds: string[],
): string[] =>
  categoryIds.filter((categoryId) => !isCategoryCovered(data, categoryId))

export const findEssentialsWithoutRecommendations = (
  data: LocationData,
): string[] => {
  if (!data) return []

  const essentials = data?.essentialCategoryId || []
  const constantCategories =
    essentials.length > 0 ? ['LI', 'CO', 'LE'] : ['LE', 'CY']

  const essentialsWithConstantCategories = uniq(
    essentials.concat(constantCategories),
  )

  const essentialsWithoutRecommendations = getUncoveredCategories(
    data,
    essentialsWithConstantCategories,
  )

  if (essentialsWithoutRecommendations.length === 0) {
    const remainingCategories = Object.keys(CategoryCardConfig).filter(
      (key) => !['default', 'FL', 'LI'].includes(key),
    )

    return getUncoveredCategories(data, remainingCategories)
  }

  return essentialsWithoutRecommendations
}
